// @ts-ignore
import React, { memo, useCallback, useEffect } from 'react';
import { BemMethod, createBem, isBrowser } from '@om-fe/utils';
import { MixedGroupedOutrightsItem } from './mixed-grouped-outrights-item';
import { MixedGroupedBettingTypesItem } from './mixed-grouped-betting-types-item';
import { useI18NContext } from '@om-fe/core/core/i18n/useI18ncontext';
import { DisciplineRouteType } from '@om-fe/core/components/discipline-anchor/discipline-route.utils';
import { UseSimpleStore, useSimpleStoreList } from '@om-fe/store';
import { MixedGroupedTournamentModel } from '../../mixed-grouped.types';
import { SCROLL_CONTAINER_TOP_CLASS } from '@om-fe/core/utils/dom';
import { setVirtualStateFocusVisible } from '@om-fe/core/core/routing/custom-routes/custom-routes.utils';
import { getCurrentVirtualState, setVirtualStateAction, VirtualStateModel } from '@om-fe/core/core/routing/custom-routes/custom-routes.utils';
import { scrollContainerToPosition } from '@om-fe/core/utils/dom';
import getConfig, { OMFE2ConfigModel, OutrightBettingTypesConfig } from '@om-fe/core/utils/config';
import { ALL_TOURNAMENT_ID } from '@om-fe/core/components/nav-secondary/parent-nav/parent-nav-constants';

const bem: BemMethod = createBem('MixedGroupedOutrights');

export interface MixedGroupedOutrightsProps {
    readonly useStore: UseSimpleStore<MixedGroupedTournamentModel>;
    readonly sportPageType: DisciplineRouteType;
    readonly tournamentId?: string;
}

export const MixedGroupedOutrights = memo((
    {
        useStore,
        sportPageType,
        tournamentId,
    }: MixedGroupedOutrightsProps,
) => {
    const list: string[] = useSimpleStoreList(useStore);
    const filteredList: string[] = tournamentId && tournamentId !== ALL_TOURNAMENT_ID ? list.filter((id) => id === tournamentId) : list;
    const { gettext } = useI18NContext();
    let timeoutId: NodeJS.Timeout | undefined = undefined;
    const config: OMFE2ConfigModel = getConfig();
    const outrightBettingTypes: OutrightBettingTypesConfig | undefined = config.outrightBettingTypes;
    const outrightBettingTypeIds: string[] = outrightBettingTypes?.enabled ? (outrightBettingTypes.tournamentIds ?? []) : [];

    const handleScrollCallback = useCallback(() => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
            setVirtualStateFocusVisible(`.${SCROLL_CONTAINER_TOP_CLASS} .TournamentOutrights__Market`, '.TournamentOutrights__Legend .TournamentOutrights__Title');
        }, 500);
    }, []);

    useEffect(() => {
        setVirtualStateAction(false);
    }, []);

    useEffect(() => {
        if (isBrowser() && config.virtualState) {
            const scrollableElementList = document.querySelectorAll(`.${SCROLL_CONTAINER_TOP_CLASS} `);
      
            const handleScroll = () => {
              scrollableElementList.forEach((element) => {
                element.addEventListener('scroll', handleScrollCallback);
              });
            };
          
            const cleanupScrollEventListeners = () => {
              scrollableElementList.forEach((element) => {
                element.removeEventListener('scroll', handleScrollCallback);
              });
            };
          
            handleScroll();
          
            return cleanupScrollEventListeners;
        }
    }, [handleScrollCallback]);

    if (!filteredList.length) {
        return (
            <div
                className={bem({ extra: 'EmptyList' })}
            >
                {gettext('No outrights currently available')}
            </div>
        );
    }

    const virtualState: VirtualStateModel = getCurrentVirtualState();
    if (config.virtualState && virtualState && !virtualState.action && virtualState.extraParams?.lastBettingOfferId && virtualState.extraParams?.focusFromTop) {
        setTimeout(() => {
            scrollContainerToPosition(`button[data-ubt-label*='${virtualState.extraParams.lastBettingOfferId}']`, virtualState.extraParams.focusFromTop);
            setVirtualStateAction();
        }, 1000);
    }

    return (
        <div className={bem()}>
            {filteredList.map((id: string, index: number) => {
                // INC-117302: lazy-loads odds per betting type for tournaments in outrightBettingTypesTournamentIds config
                const ItemComponent = outrightBettingTypeIds.includes(id)
                    ? MixedGroupedBettingTypesItem
                    : MixedGroupedOutrightsItem;
                return (
                    <ItemComponent
                        key={id}
                        useStore={useStore}
                        id={id}
                        sportPageType={sportPageType}
                        count={index}
                    />
                );
            })}
        </div>
    );
});
